//
//  QaProgramChecklistView.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the compact QA Program Checklist used by the pinned panel.
//

import { Group, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMemo, useState, type FC } from "react";

import {
    QaProgramChecklistCompletionState,
    type TrialQaProgramChecklistItemNote,
    type TrialQaProgramChecklistView as ChecklistView
} from "src/api/Tmd.dtos";

import QaProgramChecklistCompletionControl from "./QaProgramChecklistCompletionControl";
import QaProgramChecklistInfoPopover from "./QaProgramChecklistInfoPopover";
import QaProgramChecklistActivityControl from "./QaProgramChecklistActivityControl";
import QaProgramChecklistActivityDrawer from "./QaProgramChecklistActivityDrawer";
import { findQaProgramChecklistItem } from "./qaProgramChecklistRows";

/** Defines the data and mutations the pinned checklist panel works from. */
export interface QaProgramChecklistViewProps {
    checklist: ChecklistView;
    canUpdate: boolean;
    showCompleted?: boolean;
    onCompletionChange: (
        itemId: number,
        state: QaProgramChecklistCompletionState
    ) => Promise<unknown>;
    onAddNote: (note: TrialQaProgramChecklistItemNote) => Promise<unknown>;
}

/**
 * Renders the checklist as a narrow stacked list rather than the page's grid.
 *
 * Postconditions: At panel width a task's controls stack under its name; the notes drawer opens over
 * the top of the panel rather than expanding a row. Categories with no visible tasks are omitted.
 */
const QaProgramChecklistView: FC<QaProgramChecklistViewProps> = ({
    checklist,
    canUpdate,
    showCompleted = true,
    onCompletionChange,
    onAddNote
}) => {
    const [selectedItemId, setSelectedItemId] = useState<number>();
    const [activityOpened, activityHandlers] = useDisclosure(false);

    const categories = useMemo(
        () =>
            (checklist.categories ?? [])
                // The checklist is fetched whole so every view shares one cache entry; the panel
                // is an at-a-glance list of live work, so it drops inactive rows here.
                .filter(category => category.active !== false)
                .map(category => ({
                    ...category,
                    items: (category.items ?? []).filter(
                        item =>
                            item.active !== false &&
                            (showCompleted ||
                                (item.completionState ?? QaProgramChecklistCompletionState.No) ===
                                    QaProgramChecklistCompletionState.No)
                    )
                }))
                .filter(category => category.items.length > 0),
        [checklist.categories, showCompleted]
    );
    const selected = useMemo(
        () => findQaProgramChecklistItem(checklist, selectedItemId),
        [checklist, selectedItemId]
    );

    return (
        <Stack gap="sm">
            {categories.map(category => (
                <Stack
                    key={category.id}
                    gap={6}
                >
                    <Text
                        size="xs"
                        fw={600}
                        tt="uppercase"
                        c="dimmed"
                    >
                        {category.name}
                    </Text>
                    {category.items.map(item => (
                        <Stack
                            key={item.id}
                            gap={6}
                            p="xs"
                            bg="var(--mantine-color-default-hover)"
                            style={{ borderRadius: "var(--mantine-radius-sm)" }}
                        >
                            <Group
                                justify="space-between"
                                align="flex-start"
                                wrap="nowrap"
                            >
                                <Text size="sm">{item.task}</Text>
                                <Group
                                    gap={2}
                                    wrap="nowrap"
                                >
                                    {item.additionalInfo && (
                                        <QaProgramChecklistInfoPopover
                                            task={item.task}
                                            additionalInfo={item.additionalInfo}
                                        />
                                    )}
                                    <QaProgramChecklistActivityControl
                                        task={item.task}
                                        activityCount={
                                            item.activityCount ??
                                            (item.notes?.length ?? 0) +
                                                (item.completionStateChanges?.length ?? 0)
                                        }
                                        notes={item.notes ?? []}
                                        completionStateChanges={item.completionStateChanges ?? []}
                                        onOpenActivity={() => {
                                            setSelectedItemId(item.id);
                                            activityHandlers.open();
                                        }}
                                    />
                                </Group>
                            </Group>
                            <QaProgramChecklistCompletionControl
                                itemId={item.id}
                                task={item.task}
                                completionState={
                                    item.completionState ?? QaProgramChecklistCompletionState.No
                                }
                                disabled={!canUpdate}
                                fullWidth
                                onCompletionChange={onCompletionChange}
                            />
                        </Stack>
                    ))}
                </Stack>
            ))}

            {categories.length === 0 && (
                <Text
                    size="sm"
                    c="dimmed"
                >
                    Nothing outstanding &mdash; every applicable task is done.
                </Text>
            )}

            <QaProgramChecklistActivityDrawer
                opened={activityOpened}
                onClose={activityHandlers.close}
                item={selected?.item}
                categoryName={selected?.categoryName}
                canUpdate={canUpdate}
                onAddNote={onAddNote}
            />
        </Stack>
    );
};

export default QaProgramChecklistView;
